// src/components/Account/HelpSupport/PrivacyPolicy.tsx
import React from 'react';
 
const C = {
    primary:          '#6a37d4',
    primaryContainer: '#ede8ff',
    surface:          '#f3f6ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.35rem', fontWeight: '900', color: C.onBackground, margin: '40px 0 12px 0', paddingBottom: '8px', borderBottom: `2px solid ${C.surfaceHigh}` }}>
        {children}
    </h2>
);
const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.05rem', fontWeight: '800', color: C.primary, margin: '24px 0 8px 0' }}>
        {children}
    </h3>
);
const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, margin: '0 0 14px 0', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {children}
    </p>
);
const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{children}</ul>
);
const LI: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li style={{ fontSize: '0.95rem', lineHeight: '1.8', color: C.onSurface, marginBottom: '6px' }}>{children}</li>
);
const FooterLinks: React.FC<{ links: { label: string; href: string }[] }> = ({ links }) => (
    <div style={{ marginTop: '48px', paddingTop: '28px', borderTop: `1px solid ${C.surfaceHigh}`, display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {links.map(({ label, href }) => (
            <a key={label} href={href} style={{ color: C.primary, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
            >
                {label}
            </a>
        ))}
    </div>
);
 
const PrivacyPolicy: React.FC = () => (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '48px 24px 80px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
 
            {/* Header */}
            <div style={{ marginBottom: '40px' }}>
                <div style={{ display: 'inline-block', background: C.primaryContainer, color: C.primary, padding: '4px 14px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: "'Epilogue', sans-serif", marginBottom: '16px' }}>
                    Legal
                </div>
                <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                    Privacy Policy
                </h1>
                <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0 }}>Last updated: November 28, 2025</p>
            </div>
 
            {/* Card */}
            <div style={{ background: '#fff', borderRadius: '20px', padding: '48px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: `1px solid ${C.surfaceHigh}` }}>
 
                <P>This Privacy Policy describes how Scriblish collects, uses, and protects personal information from children and their parents/guardians. We comply with COPPA and are committed to protecting children's privacy and safety online.</P>
 
                <H2>1. Information We Collect</H2>
                <H3>1.1 Information from Children</H3>
                <P>With verifiable parental consent, we collect the following from children under 13:</P>
                <UL>
                    <LI>Account Information: Username, age range, avatar/profile picture (optional)</LI>
                    <LI>User-Generated Content: Stories, illustrations, book covers, comments, and messages</LI>
                    <LI>Usage Data: Activity logs, feature usage, badges earned, writing progress</LI>
                    <LI>Technical Data: Device type, browser type, operating system, IP address (anonymized)</LI>
                </UL>
 
                <H3>1.2 Information from Parents / Guardians</H3>
                <UL>
                    <LI>Contact Information: Email address, name</LI>
                    <LI>Account Information: Username, password</LI>
                    <LI>Payment Information: Credit card details (processed securely through third-party payment processors)</LI>
                    <LI>Consent Records: Documentation of parental consent for child accounts</LI>
                </UL>
 
                <H3>1.3 Information We Do NOT Collect</H3>
                <UL>
                    <LI>Children's real names (usernames must be pseudonymous)</LI>
                    <LI>Physical addresses or precise geolocation data</LI>
                    <LI>Phone numbers</LI>
                    <LI>School names or educational records</LI>
                    <LI>Social security numbers or government IDs</LI>
                    <LI>Photos or videos of children (only drawings/avatars allowed)</LI>
                </UL>
 
                <H2>2. How We Use Information</H2>
                <H3>2.1 To Provide the Service</H3>
                <UL>
                    <LI>Create and manage user accounts</LI>
                    <LI>Enable story creation, editing, and sharing</LI>
                    <LI>Display user content according to privacy settings</LI>
                    <LI>Track progress, awards, and achievements</LI>
                    <LI>Facilitate collaboration on shared projects</LI>
                </UL>
 
                <H3>2.2 To Ensure Safety</H3>
                <UL>
                    <LI>Moderate content for appropriateness and safety</LI>
                    <LI>Detect and prevent prohibited content (personal information, inappropriate language)</LI>
                    <LI>Investigate and respond to safety reports</LI>
                    <LI>Enforce our Terms of Service and Community Guidelines</LI>
                </UL>
 
                <H3>2.3 What We Do NOT Do</H3>
                <UL>
                    <LI>Sell or rent personal information</LI>
                    <LI>Use student data for advertising or marketing</LI>
                    <LI>Create behavioral profiles for commercial purposes</LI>
                    <LI>Share data with third parties except as described in Section 4</LI>
                </UL>
 
                <H2>3. Data Storage and Security</H2>
                <H3>3.1 Security Measures</H3>
                <P>We implement industry-standard security practices:</P>
                <UL>
                    <LI>Encryption: All data is encrypted in transit (TLS/SSL) and at rest (AES-256)</LI>
                    <LI>Access Controls: Strict employee access limits with two-factor authentication</LI>
                    <LI>Secure Infrastructure: Cloud hosting with enterprise-grade security (Firebase/Google Cloud)</LI>
                    <LI>Regular Audits: Quarterly security assessments and vulnerability testing</LI>
                    <LI>Automated Monitoring: Real-time threat detection and intrusion prevention</LI>
                </UL>
 
                <H3>3.2 Data Retention</H3>
                <UL>
                    <LI>Active Accounts: Data retained while account is active</LI>
                    <LI>Inactive Accounts: Deleted after 2 years of inactivity (with parental notification)</LI>
                    <LI>Deleted Accounts: Personal data permanently deleted within 30 days</LI>
                    <LI>Legal Requirements: Some data may be retained longer if required by law</LI>
                </UL>
 
                <H2>4. Information Sharing and Disclosure</H2>
                <H3>4.1 We Do NOT Sell Data</H3>
                <P>We never sell, rent, or trade children's personal information to third parties for marketing purposes.</P>
 
                <H3>4.2 Service Providers</H3>
                <P>We share limited data with trusted service providers who help us operate the platform:</P>
                <UL>
                    <LI>Cloud Hosting: Google Cloud Platform / Firebase (data storage and infrastructure)</LI>
                    <LI>Payment Processing: Stripe (payment information only, not shared with us)</LI>
                    <LI>Email Services: SendGrid (for transactional emails to parents)</LI>
                    <LI>Analytics: Google Analytics (with anonymized IPs and no personal identifiers)</LI>
                </UL>
                <P>All service providers are contractually required to protect data and use it only for specified purposes.</P>
 
                <H2>5. Parental Rights and Controls (COPPA)</H2>
                <H3>5.1 Parental Consent</H3>
                <P>Before collecting personal information from children under 13, we obtain verifiable parental consent through email confirmation with a unique consent link or credit card verification.</P>
 
                <H3>5.2 Parent Access and Control</H3>
                <P>Parents have the right to:</P>
                <UL>
                    <LI>Review: Access all information collected about their child</LI>
                    <LI>Update: Correct or modify their child's information</LI>
                    <LI>Delete: Request deletion of their child's account and data</LI>
                    <LI>Refuse Further Collection: Revoke consent and prevent future data collection</LI>
                    <LI>Control Sharing: Manage who can view their child's content</LI>
                    <LI>Monitor Activity: View activity logs, stories, and interactions</LI>
                </UL>
 
                <H2>6. Additional Children's Privacy Protections</H2>
                <H3>6.1 No Behavioral Advertising</H3>
                <P>We do not serve targeted advertisements to children or collect personal information for advertising purposes.</P>
 
                <H3>6.2 Content Moderation</H3>
                <P>All shared content is:</P>
                <UL>
                    <LI>Screened by AI for personal information and inappropriate content</LI>
                    <LI>Reviewed by human moderators before publication</LI>
                    <LI>Subject to user reporting and parent review</LI>
                </UL>
 
                <H2>7. Visual Assets & Licensing Policy</H2>
                <H3>7.1 Overview</H3>
                <P>Our platform uses a combination of original artwork, licensed illustrations, and royalty-free images to help kids design creative book covers and stories.</P>
 
                <H3>7.2 Sources of Visual Assets</H3>
                <UL>
                    <LI>Backgrounds and illustrations are sourced from Pixabay, Openclipart, and other platforms providing royalty-free content for commercial use</LI>
                    <LI>All visual materials are used in accordance with each platform's content license</LI>
                    <LI>We only use media that is free to modify and redistribute within new, creative projects</LI>
                </UL>
 
                <H3>7.3 Rights Holder Contact</H3>
                <P>
                    If you are an artist or rights holder and believe your content has been used in error, please contact us at{' '}
                    <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>scriblishofficial@gmail.com</a>
                </P>
 
                <H2>8. Cookies and Tracking Technologies</H2>
                <H3>8.1 Types of Cookies We Use</H3>
                <UL>
                    <LI>Essential Cookies: Required for login, session management, and security</LI>
                    <LI>Functional Cookies: Remember user preferences and settings</LI>
                    <LI>Analytics Cookies: Understand how users interact with the platform (anonymized)</LI>
                </UL>
 
                <H3>8.2 Cookie Management</H3>
                <P>Parents can control cookie settings in their browser, delete cookies at any time, or disable analytics cookies (essential cookies required for service).</P>
 
                <H2>9. Changes to This Privacy Policy</H2>
                <P>When we make changes:</P>
                <UL>
                    <LI>We will update the "Last Updated" date</LI>
                    <LI>For material changes, we will notify parents via email</LI>
                    <LI>For changes affecting children's data collection, we will obtain new parental consent</LI>
                </UL>
 
                <H2>10. Contact Us</H2>
                <P>
                    For privacy questions, concerns, or to exercise your rights, please contact us at{' '}
                    <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>scriblishofficial@gmail.com</a>
                </P>
                <P>We respond to privacy inquiries within 48 hours and fulfill data requests within 30 days.</P>
 
                <H2>11. Certifications and Compliance</H2>
                <P>Scriblish is committed to compliance with:</P>
                <UL>
                    <LI>COPPA (Children's Online Privacy Protection Act) — USA</LI>
                    <LI>FERPA (Family Educational Rights and Privacy Act) — USA</LI>
                    <LI>GDPR (General Data Protection Regulation) — EU</LI>
                    <LI>CCPA (California Consumer Privacy Act) — California</LI>
                </UL>
 
                <FooterLinks links={[
                    { label: 'About',                href: '/about' },
                    { label: 'Community Guidelines', href: '/community-guidelines' },
                    { label: 'Terms of Service',     href: '/terms-of-service' },
                    { label: 'Contact Us',           href: '/contact' },
                ]} />
            </div>
        </div>
    </div>
);
 
export default PrivacyPolicy;